import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './hooks/useAuth';
import { apiFetch } from './utils/api';
import ReportTableHeader from './components/ReportTableHeader';
import './UserReportPage.css';

/* ── Helper maps ───────────────────────────────────────────── */
// Product-type colors (CMO 05): deliberately NOT red/amber/green — those implied
// drug=danger / cosmetic=good. Risk colors (below) stay separate.
//   전문의약품 DRUG → Sky Blue · 일반의약품 OTC → Mint · 화장품 COSMETIC → Pink
const getClassColor = (cls) => ({ drug: '#0284C7', otc_drug: '#0D9488', cosmetic: '#DB2777' }[cls?.toLowerCase()] || '#607d8b');
const getRiskColor = (risk) => ({ high: '#f44336', medium: '#ff9800', low: '#4caf50' }[risk?.toLowerCase()] || '#607d8b');

/* ── Main UserReportPage ───────────────────────────────────── */
export default function UserReportPage() {
  const navigate = useNavigate();
  const { isSiteAdmin } = useAuth();
  const { i18n } = useTranslation();
  const locale = i18n.language?.startsWith('ko') ? 'ko' : 'en';
  const isKo = locale === 'ko';
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [balance, setBalance] = useState(null);
  const [membershipTier, setMembershipTier] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(null);

  const canExport = isSiteAdmin || (membershipTier && membershipTier.name !== 'trial');

  const loadReports = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, per_page: 20 });
      if (search) params.append('search', search);
      const res = await apiFetch(`/api/v1/reports?${params}`);
      if (res.ok) {
        const data = await res.json();
        setReports(data.items || []);
        setTotal(data.total || 0);
      }
    } catch { setError('Failed to load reports'); }
  }, [page, search]);

  const loadBalance = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/credits/balance');
      if (res.ok) {
        const data = await res.json();
        setBalance(data.balance);
        setMembershipTier(data.membership_tier || null);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadReports(); loadBalance(); }, [loadReports, loadBalance]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this report?')) return;
    try {
      const res = await apiFetch(`/api/v1/reports/${id}`, { method: 'DELETE' });
      if (res.ok) { setMessage('Report deleted'); loadReports(); }
      else { setError('Failed to delete'); }
    } catch { setError('Delete failed'); }
  };

  const handleDownloadPdf = async (id, title) => {
    setPdfLoading(id);
    try {
      const res = await apiFetch(`/api/v1/reports/${id}/pdf?locale=${locale}`);
      if (!res.ok) throw new Error('PDF generation failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const slug = (title || 'report').substring(0, 40).replace(/\s+/g, '_');
      a.download = `VERIPASS_Report_${slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { setError('Failed to download PDF'); }
    setPdfLoading(null);
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="urp-page">
      <div className="urp-header">
        <h1>Reports</h1>
        <div className="urp-header-right">
          {balance !== null && (
            membershipTier?.is_unlimited ? (
              <span className="rp-credit-chip unlimited">∞ Unlimited</span>
            ) : (
              /* CMO 01 — the remaining count is a button that opens the plan
                 and usage card (Settings → Subscription).

                 KG이니시스 review (T2.7): it read "{n} credits +" under the
                 title "크레딧 충전" / "Add credits" -- in English even in
                 Korean mode -- selling a top-up nothing can complete. It now
                 states a count of reports, with no "+". The class names are
                 identifiers and keep "credit" (plan §3.3). */
              <button
                type="button"
                className={`rp-credit-chip rp-credit-btn ${balance === 0 ? 'zero' : ''}`}
                onClick={() => navigate('/settings?tab=subscription')}
                title={isKo ? '구독 및 이용 현황 보기' : 'View plan and usage'}
              >
                {isKo ? `남은 분석 리포트 ${balance}건` : `${balance} reports left`}
              </button>
            )
          )}
          <button className="btn-primary" onClick={() => navigate('/')}>+ New Analysis</button>
        </div>
      </div>

      {error && <div className="rp-error">{error} <button onClick={() => setError('')}>×</button></div>}
      {message && <div className="rp-msg">{message} <button onClick={() => setMessage('')}>×</button></div>}

      <div className="rp-toolbar">
        <input placeholder="Search reports..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
      </div>

      {/* CMO 02 — sample reports: clearer labels, "Safe" → "Low Risk" */}
      <div className="urp-preview-launcher" aria-label={isKo ? '샘플 리포트 보기' : 'View sample reports'}>
        <span>{isKo ? '리포트 샘플 보기' : 'Check sample reports'}</span>
        <button type="button" className="urp-preview-btn high" onClick={() => navigate('/reports/_preview-high')}>
          {isKo ? '샘플: High Risk' : 'Sample: High Risk'}
        </button>
        <button type="button" className="urp-preview-btn medium" onClick={() => navigate('/reports/_preview-medium')}>
          {isKo ? '샘플: Medium Risk' : 'Sample: Medium Risk'}
        </button>
        <button type="button" className="urp-preview-btn low" onClick={() => navigate('/reports/_preview-low')}>
          {isKo ? '샘플: Low Risk' : 'Sample: Low Risk'}
        </button>
      </div>

      <table className="rp-table">
        <ReportTableHeader />
        <tbody>
          {reports.map(r => (
            <tr key={r.id}>
              <td>{new Date(r.created_at).toLocaleDateString()}</td>
              <td className="title-cell" onClick={() => navigate(`/reports/${r.id}`)}>
                {r.title || 'Untitled Report'}
              </td>
              <td className="rp-col-product-type">
                <span className="class-badge" style={{ background: getClassColor(r.analysis_result?.classification) }}>
                  {r.analysis_result?.classification || '—'}
                </span>
              </td>
              <td className="rp-col-risk">
                <span className="risk-badge" style={{ background: getRiskColor(r.analysis_result?.risk_level) }}>
                  {r.analysis_result?.risk_level || '—'}
                </span>
              </td>
              <td className="actions-cell">
                <button className="btn-sm clinical" onClick={() => navigate(`/reports/${r.id}`)}>
                  Open Clinical Report
                </button>
                {/* CMO 06 — Expand replaced by Download */}
                {canExport ? (
                  <button className="btn-sm" disabled={pdfLoading === r.id} onClick={() => handleDownloadPdf(r.id, r.title)}>
                    {pdfLoading === r.id ? '⏳ Generating…' : '📄 Download'}
                  </button>
                ) : (
                  <button className="btn-sm rp-export-locked" disabled title="Starter membership or above required">
                    🔒 Download
                  </button>
                )}
                <button className="btn-sm danger" onClick={() => handleDelete(r.id)}>Delete</button>
              </td>
            </tr>
          ))}
          {reports.length === 0 && (
            <tr><td colSpan={5} className="empty-cell">
              No reports yet. Click "+ New Analysis" to analyze a claim.
            </td></tr>
          )}
        </tbody>
      </table>

      {totalPages > 1 && (
        <div className="rp-pagination">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>←</button>
          <span>{page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>→</button>
        </div>
      )}
    </div>
  );
}
